import * as yup from 'yup'

import { trimString } from 'common/utils/yupSchemaUtils'
import { validHttpsUrlRegex } from 'common/utils/fileUploading/validators'
import { POST_MAX_LENGTH } from '../communityConstants'
import { BUTTON_TYPES, GROUP_CALL_TYPE, REPLY_BUTTON_TYPES } from '../postButtonUtils'
import { isValidTimeZone } from '../timeZoneUtils'

const buttonTargetRules = {
  // Trimmed because a link is almost always pasted, and a paste carries stray
  // whitespace the URL check would otherwise reject.
  link: yup
    .string()
    .trim()
    .when('buttonType', {
      is: (buttonType) => buttonType === 'link' || buttonType === GROUP_CALL_TYPE,
      then: (schema) =>
        schema
          .required('Link is required')
          .matches(validHttpsUrlRegex, { message: 'Link must include https and be a valid URL' }),
    }),
  scene: yup.string().when('buttonType', {
    is: 'scene',
    then: (schema) => schema.required('App page is required'),
  }),
  videoId: yup.string().when('buttonType', {
    is: 'video',
    then: (schema) => schema.required('Video is required'),
  }),
  programId: yup.string().when('buttonType', {
    is: (buttonType) => buttonType === 'program' || buttonType === 'workout',
    then: (schema) => schema.required('Program is required'),
  }),
  workoutId: yup.string().when('buttonType', {
    is: 'workout',
    then: (schema) => schema.required('Workout is required'),
  }),
  exerciseId: yup.string().when('buttonType', {
    is: 'exercise',
    then: (schema) => schema.required('Exercise is required'),
  }),
  collectionId: yup.string().when('buttonType', {
    is: 'collection',
    then: (schema) => schema.required('Collection is required'),
  }),
  programGroupId: yup.string().when('buttonType', {
    is: 'programGroup',
    then: (schema) => schema.required('Series is required'),
  }),
}

export const schema = yup
  .object({
    text: yup
      .string()
      .transform(trimString)
      .required('Post text is required')
      .max(POST_MAX_LENGTH, `Post should be no longer than ${POST_MAX_LENGTH} characters`),
    buttonType: yup.string().oneOf(BUTTON_TYPES.map(({ value }) => value)),
    ...buttonTargetRules,
    callTimeZone: yup.string().when('buttonType', {
      is: GROUP_CALL_TYPE,
      then: (schema) =>
        schema
          .required('Timezone is required')
          .test('valid-timezone', 'Timezone must be valid', (value) => isValidTimeZone(value)),
    }),
    callStartAt: yup.number().when('buttonType', {
      is: GROUP_CALL_TYPE,
      then: (schema) => schema.typeError('Call start is required').required('Call start is required'),
    }),
    callEndAt: yup.number().when('buttonType', {
      is: GROUP_CALL_TYPE,
      then: (schema) =>
        schema
          .typeError('Call end is required')
          .required('Call end is required')
          .moreThan(yup.ref('callStartAt'), 'Call end must be after call start'),
    }),
    notifyMembers: yup.boolean(),
    pinned: yup.boolean(),
    repliesOff: yup.boolean(),
  })
  .required()

export const replyAttachmentSchema = yup
  .object({
    buttonType: yup
      .string()
      .required('Content type is required')
      .oneOf(
        REPLY_BUTTON_TYPES.map(({ value }) => value),
        'Content type is required'
      ),
    ...buttonTargetRules,
    openInDefaultBrowser: yup.boolean(),
  })
  .required()
